import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, Heart, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../../context/WishlistContext';
import OptimizedImage from './OptimizedImage';

const WishlistIcon = () => {
  const { wishlistItems, totalWishlist, removeFromWishlist } = useWishlist();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const previewRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!isPreviewOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!previewRef.current?.contains(event.target)) setIsPreviewOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsPreviewOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPreviewOpen]);

  const badgeVariants = {
    visible: { 
      scale: [1, 1.2, 1],
      y: [0, -4, 0],
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  return (
    <div ref={previewRef} className="relative">
      <motion.button
        type="button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsPreviewOpen(!isPreviewOpen)}
        aria-label={`Ouvrir la liste de souhaits, ${totalWishlist} article${totalWishlist > 1 ? 's' : ''}`}
        aria-expanded={isPreviewOpen}
        aria-haspopup="dialog"
        className="cab-nav__icon-button relative"
      >
        <Heart size={18} className="transition-colors duration-300" fill={totalWishlist > 0 ? 'currentColor' : 'none'} />
        {totalWishlist > 0 && (
          <motion.span
            className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#f5c400] px-1 text-[10px] font-black text-black"
            variants={badgeVariants}
            initial="visible"
            animate="visible"
          >
            {totalWishlist > 99 ? '99+' : totalWishlist}
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>
        {isPreviewOpen && (
          <motion.div
            role="dialog"
            aria-label="Aperçu de la liste de souhaits"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-full z-[80] mt-3 w-[min(360px,calc(100vw-24px))] overflow-hidden border border-white/15 bg-[#10100f] text-white shadow-[0_24px_70px_rgba(0,0,0,.6)]"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div>
                <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#f5c400]">CAB STORE / FAVORIS</span>
                <h3 className="mt-1 text-lg font-semibold">Ma liste <span className="text-sm font-normal text-white/45">({totalWishlist})</span></h3>
              </div>
              <button
                type="button"
                onClick={() => setIsPreviewOpen(false)}
                aria-label="Fermer l’aperçu"
                className="grid h-9 w-9 place-items-center border border-white/10 text-white/60 transition hover:border-white/30 hover:text-white"
              >
                <X size={17} />
              </button>
            </div>
            
            <div className="max-h-[300px] overflow-y-auto px-5">
              <AnimatePresence initial={false} mode="popLayout">
                {wishlistItems.slice(0, 3).map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: 22, scale: 0.96, height: 0, paddingTop: 0, paddingBottom: 0, filter: 'blur(5px)' }}
                    transition={{ duration: prefersReducedMotion ? 0.12 : 0.34, ease: [0.22, 1, 0.36, 1], layout: { duration: prefersReducedMotion ? 0.12 : 0.38 } }}
                    className="flex items-center gap-3 overflow-hidden border-b border-white/10 py-3 last:border-b-0"
                  >
                    <Link to={`/product/${item.productId || item.id}`} onClick={() => setIsPreviewOpen(false)} className="grid h-[66px] w-[58px] shrink-0 place-items-center overflow-hidden bg-[#e9e8e3]">
                      <OptimizedImage src={item.image || item.images?.[0] || '/assets/home 1.png'} alt="" className="h-full w-full object-contain p-1" sizes="64px" />
                    </Link>
                    <div className="min-w-0 flex-1">
                      <Link to={`/product/${item.productId || item.id}`} onClick={() => setIsPreviewOpen(false)} className="block truncate text-[12px] font-semibold text-white hover:text-[#f5c400]">{item.name}</Link>
                      <p className="mt-1 text-[11px] text-white/50">{item.price} TND{item.size ? ` · ${item.size}` : ''}</p>
                    </div>
                    <button type="button" onClick={() => removeFromWishlist(item.id)} aria-label={`Retirer ${item.name} des favoris`} className="grid h-8 w-8 shrink-0 place-items-center text-white/40 transition hover:bg-white/5 hover:text-white">
                      <Trash2 size={15} />
                    </button>
                  </motion.div>
                ))}
                {wishlistItems.length === 0 && (
                  <motion.div
                    key="wishlist-preview-empty"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: prefersReducedMotion ? 0.12 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="py-9 text-center"
                  >
                    <Heart size={24} className="mx-auto mb-3 text-white/35" />
                    <p className="text-sm font-medium">Votre liste est encore vide</p>
                    <p className="mt-1 text-xs text-white/45">Gardez ici les pièces qui vous plaisent.</p>
                  </motion.div>
                )}
                {wishlistItems.length > 3 && (
                  <motion.p layout key="wishlist-preview-more" className="py-3 text-center text-[11px] text-white/45">+ {wishlistItems.length - 3} autres pièces</motion.p>
                )}
              </AnimatePresence>
            </div>
            
            <div className="border-t border-white/10 p-4">
              <Link to="/wishlist" onClick={() => setIsPreviewOpen(false)} className="flex min-h-11 items-center justify-center gap-2 bg-[#f5c400] px-4 text-[10px] font-bold uppercase tracking-[0.12em] text-black transition hover:bg-[#ffda36]">
                Voir tous mes favoris <ArrowRight size={15} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WishlistIcon;
